import { Box, Typography } from "@mui/material";
import { LayoutOnboarding, LoadingBackdrop } from "../../../../components";
import React, { useEffect, useLayoutEffect } from "react";
import { useOtpSignUp, useOtpVerify } from "../../../../services/user/user-otp.service";

import { IRootState } from "../../../../state";
import { IUserState } from "../../../../state/user";
import { MIXPANEL_EVENTS } from "../../../../config/mixPanelEvents";
import { OTPVerifyForm } from "../../forms";
import { PAGES_PATH } from "../../../../navigation/params";
import { useLocation, useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useUserStatusStore } from "../../../../hooks/redux-hooks/user-status/user-status.store";
import { userActivityLogger } from "../../../../utils/mixpanel";
import { useUserCreateStore } from "../../../../hooks/redux-hooks/user-create-store/user-create.store";
import { useHospitalDetailsListStore } from
  "../../../../hooks/redux-hooks/hospital-details-list-store/hospital-details-list-store";
import { useBookingAppointmentTypeStore } from "../../../../hooks";
import { useOtpVerificaton } from "../../../../services/otp-services/otp.services";
import { useUserStore } from "../../../../hooks/redux-hooks/user-store/user.store";

type Props = {}

const OTPVerificationContainer = (props: Props) => {
  const [searchParams] = useSearchParams();
  const { setCreateProfile } = useUserCreateStore();
  const testingToken = searchParams.get("testingToken");
  const autologinEmail = searchParams.get("autologin");
  const {
    emailSignIn,
    phoneSignIn,
    user,
  } = useSelector((state: IRootState) => state.user as IUserState);
  const { profileUser } = useUserStore();
  const contactId = user?.contact_id;
  const isSignupFlow=localStorage.getItem("signUp");
  const { verifyOTP, data, error, loading } = useOtpVerify();
  const { handlePhoneEnableOtpVerify, verifyOtpData, verifyOtpLoading, verifyOtpError } = useOtpVerificaton();
  const { sendOTP, data: otpSentData } = useOtpSignUp();
  const { setEmailSignIn, setPhoneSignIn } = useUserStatusStore();
  const navigate = useNavigate();
  const redirectionPath = localStorage.getItem("redirectionPath");
  const { setIsHospitalListUpdated } = useHospitalDetailsListStore();
  const { setIsServiceStatusUpdated } = useBookingAppointmentTypeStore();
  const location = useLocation();
  const enableOTPSignUpMethod:boolean = location.state?.verifyOtpSignUp;
  const isFromAddPhoneNumber:boolean = location.state?.isFromAddPhoneNumber;

  const handleSubmit = (data: {otp: string }) => {
    if (emailSignIn && phoneSignIn && profileUser?.contact_id) {
      const inputData = { phone: phoneSignIn, otp: parseInt(data.otp) };

      handlePhoneEnableOtpVerify(inputData);

      return;
    }
    if (emailSignIn && !isFromAddPhoneNumber) {
      const inputData = { email: emailSignIn, otp: parseInt(data.otp) };

      verifyOTP(inputData);
    }
    if (phoneSignIn) {
      const inputData = { phone: phoneSignIn, otp: parseInt(data.otp) };

      if (enableOTPSignUpMethod) {
        handlePhoneEnableOtpVerify(inputData);
      } else {
        verifyOTP(inputData);
      }
    };
  };

  useEffect(() => {
    if (verifyOtpData) {
      if (profileUser?.contact_id) {
        navigate(PAGES_PATH.DASHBOARD, { state: { existingUserFromEzyVet: true } });

        return;
      }
      navigate(PAGES_PATH.PROFILE_COMPLETION, { state: { phone: phoneSignIn, verifyOtpSignUp: undefined } });
      userActivityLogger(MIXPANEL_EVENTS.USER_MOBILE_VERIFICATION_SUCCESS,
        { signin_type: "mobile", user_type: "new_user", mobile_signin_enabled: true, mobile_number: phoneSignIn });
    }
  }, [verifyOtpData]);

  useEffect(()=> {
    if (data) {
      setIsHospitalListUpdated(false);
      setIsServiceStatusUpdated(false);
      if (data.user) {
        if (data.user.contact_id === "" || !data.user.contact_id) {
          redirectionPath && localStorage.removeItem("redirectionPath");
          setCreateProfile(null);
          if (!data.user.phone && !data.user.fireBasePhone) {
            navigate(PAGES_PATH.ADD_PHONE_NUMBER);
          } else {
            navigate(PAGES_PATH.PROFILE_COMPLETION, {
              state: undefined });
          }
        } else {
          navigate(PAGES_PATH.DASHBOARD);
        }
      } else {
        navigate(PAGES_PATH.ADD_PHONE_NUMBER);
      }
    }
  }, [data]);

  const handleResendOtp = () => {
    if (phoneSignIn) {
      if (testingToken && process.env.REACT_APP_NODE_ENV!=="production") {
        const updatedData = { phone: phoneSignIn, testingToken: testingToken };

        sendOTP(updatedData);
      } else {
        sendOTP({ phone: phoneSignIn });
        if (enableOTPSignUpMethod) {
          userActivityLogger(MIXPANEL_EVENTS.USER_REQUEST_RESEND_OTP_MOBILE_VERIFICATION,
            { signin_type: "mobile", user_type: "new_user", mobile_number: phoneSignIn });
        } else {
          userActivityLogger(MIXPANEL_EVENTS.RESEND_OTP,
            { phone: phoneSignIn, signin_type: "mobile", user_type: isSignupFlow?"new_user":"existing_user" },
            { email: emailSignIn as string });
        }
      }
    } else if (emailSignIn) {
      sendOTP({ email: emailSignIn });
      userActivityLogger(MIXPANEL_EVENTS.RESEND_OTP,
        { email: emailSignIn, signin_type: "email", user_type: isSignupFlow ?"new_user":"existing_user" },
        { email: emailSignIn as string });
    }
  };

  useEffect(()=>{
    if (enableOTPSignUpMethod) {
      return; // disable the state clearing for new user when signing up through mobile number
    }
    if (phoneSignIn) {
      setEmailSignIn("");
    } else if (emailSignIn) {
      setPhoneSignIn("");
    }
  }, []);

  useEffect(()=>{
    if ((!phoneSignIn && !emailSignIn && !autologinEmail) || (contactId && !isFromAddPhoneNumber)) {
      navigate("/");
    }
  }, []);

  useEffect(() => {
    if (data) {
      if (phoneSignIn) {
        userActivityLogger(MIXPANEL_EVENTS.USER_SIGNIN_SUCCESS,
          { signin_type: "mobile", user_type: isSignupFlow ?"new_user":"existing_user" },
          { phone: phoneSignIn },
        );
      } else {
        userActivityLogger(MIXPANEL_EVENTS.USER_SIGNIN_SUCCESS,
          { signin_type: "email", user_type: isSignupFlow ?"new_user":"existing_user" },
          { email: emailSignIn?.toString() },
        );
      }
      localStorage.removeItem("signUp");
    }
    if (error) {
      if (phoneSignIn) {
        if (enableOTPSignUpMethod) {
          // mobile
          userActivityLogger(MIXPANEL_EVENTS.USER_MOBILE_VERIFICATION_FAILURE,
            { error_message: error.graphQLErrors[0].message,
              signin_type: "mobile", user_type: "new_user", mobile_number: phoneSignIn },
            { email: emailSignIn as string });
        } else {
          userActivityLogger(MIXPANEL_EVENTS.USER_SIGNIN_FAILURE, {
            error_message: error.graphQLErrors[0].message,
            user_type: isSignupFlow ?"new_user":"existing_user",
            signin_type: "mobile",
          }, { phone: phoneSignIn });
        }
      } else {
        userActivityLogger(MIXPANEL_EVENTS.USER_SIGNIN_FAILURE, {
          error_message: error.graphQLErrors[0].message,
          user_type: isSignupFlow ?"new_user":"existing_user",
          signin_type: "email",
        }, { email: emailSignIn?.toString() });
      }
    }
  }, [data, error]);

  useLayoutEffect(() => {
    if (autologinEmail) {
      setEmailSignIn(autologinEmail);
      setPhoneSignIn("");
      sendOTP({ email: autologinEmail });
      localStorage.removeItem("isWebUserApiCalled");
    }
  }, [autologinEmail]);

  return (
    <>
      {(loading || verifyOtpLoading) &&
          <LoadingBackdrop />
      }
      <LayoutOnboarding image="/assets/icons/welcome_screen.jpg">
        <Box className="flex flex-col items-center w-full gap-2">
          <Typography sx={{ mb: 1 }} className="!font-bold !text-2xl 2xl:!text-3xl">Enter Verification Code</Typography>
          <Typography sx={{ mb: 1 }} className="!text-base">We sent a 6-digit code to</Typography>
          <Typography variant="h2" className="bg-taupe-15 w-full p-1 text-center !font-bold rounded-xl" mb={3}>
            {enableOTPSignUpMethod ? phoneSignIn : emailSignIn || phoneSignIn}</Typography>
          <OTPVerifyForm onSubmit={handleSubmit}
            length={6} handleResendOtp={handleResendOtp} otpSentData={otpSentData}
            error={error || verifyOtpError}/>
        </Box>
      </LayoutOnboarding>
    </>
  );
};

export default OTPVerificationContainer;
